/**
 * Access-requests API client — a restricted user requests access to a task or
 * project they can't see; an approver (project owner/admin, or the task's
 * owner) approves or denies it.
 */
import { apiClient } from './index';

export type AccessResourceType = 'task' | 'project';
export type AccessRequestStatus = 'pending' | 'approved' | 'denied';

export interface AccessRequest {
  id: string;
  tenant_id: string;
  resource_type: AccessResourceType;
  resource_id: string;
  user_id: string; // the requester
  status: AccessRequestStatus;
  note?: string;
  decided_by?: string;
  decided_at?: string;
  created_at: string;
}

export async function createAccessRequest(
  resourceType: AccessResourceType,
  resourceId: string,
  note?: string,
): Promise<AccessRequest> {
  const { data } = await apiClient.post<AccessRequest>('/access-requests', {
    resource_type: resourceType,
    resource_id: resourceId,
    note: note || undefined,
  });
  return data;
}

/** Pending requests the current user is allowed to decide. */
export async function listAccessRequests(): Promise<AccessRequest[]> {
  const { data } = await apiClient.get<{ data: AccessRequest[] }>('/access-requests');
  return data.data ?? [];
}

/** Approved task grants the current user manages (read-only; no revoke endpoint yet). */
export async function listAccessGrants(): Promise<AccessRequest[]> {
  const { data } = await apiClient.get<{ data: AccessRequest[] }>('/access-requests', { params: { status: 'approved' } });
  return data.data ?? [];
}

export async function approveAccessRequest(id: string): Promise<void> {
  await apiClient.post(`/access-requests/${id}/approve`);
}

export async function denyAccessRequest(id: string): Promise<void> {
  await apiClient.post(`/access-requests/${id}/deny`);
}
